import { useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { useT } from "@/contexts/I18nContext";
import { UploadStep } from "./UploadStep";
import { MappingStep } from "./MappingStep";
import { ReviewStep } from "./ReviewStep";
import { CredentialsStep } from "./CredentialsStep";
import type {
  ColumnMap,
  CommitImportResult,
  ImportSkip,
  ParsedUpload,
  ReviewedRow,
} from "@/types";

/**
 * The import, in four steps, the third of which cannot be skipped.
 *
 * The review step is structural rather than procedural: the wizard has no path
 * from a parsed file to `commitImport` that does not pass through rows a person
 * has been shown, and the backend takes reviewed rows and nothing else. Thirty
 * accounts created from a file nobody read is the failure this shape exists to
 * make impossible.
 */

type Step = "upload" | "mapping" | "review" | "credentials";

const STEPS: readonly { id: Step; labelKey: string }[] = [
  { id: "upload", labelKey: "import.step.upload" },
  { id: "mapping", labelKey: "import.step.mapping" },
  { id: "review", labelKey: "import.step.review" },
  { id: "credentials", labelKey: "import.step.credentials" },
];

export function ImportWizard() {
  const t = useT();
  const [step, setStep] = useState<Step>("upload");
  const [upload, setUpload] = useState<ParsedUpload | undefined>(undefined);
  const [columnMap, setColumnMap] = useState<ColumnMap | undefined>(undefined);
  const [rows, setRows] = useState<ReviewedRow[]>([]);
  const [unreadable, setUnreadable] = useState<ImportSkip[]>([]);
  const [result, setResult] = useState<CommitImportResult | undefined>(undefined);

  const reached = STEPS.findIndex((entry) => entry.id === step);

  return (
    <SectionShell titleKey="import.title">
      <ol className="flex flex-wrap gap-x-6 gap-y-2 border-b border-line pb-4">
        {STEPS.map((entry, position) => (
          <li
            key={entry.id}
            aria-current={entry.id === step ? "step" : undefined}
            className={`text-sm ${
              position === reached
                ? "font-medium text-ink"
                : position < reached
                  ? "text-ink-muted"
                  : "text-ink-faint"
            }`}
          >
            <span className="mr-1.5 tabular-nums">{position + 1}</span>
            {t(entry.labelKey)}
          </li>
        ))}
      </ol>

      <div className="mt-8">
        {step === "upload" ? (
          <UploadStep
            onParsed={(parsed) => {
              setUpload(parsed);
              setStep("mapping");
            }}
          />
        ) : null}

        {step === "mapping" && upload ? (
          <MappingStep
            upload={upload}
            onReady={(map, previewRows, skipped) => {
              setColumnMap(map);
              setRows(previewRows);
              setUnreadable(skipped);
              setStep("review");
            }}
            onBack={() => setStep("upload")}
          />
        ) : null}

        {step === "review" && upload && columnMap ? (
          <ReviewStep
            upload={upload}
            rows={rows}
            unreadable={unreadable}
            onChange={setRows}
            onBack={() => setStep("mapping")}
            onCommitted={(committed) => {
              setResult(committed);
              setStep("credentials");
            }}
          />
        ) : null}

        {step === "credentials" && result ? <CredentialsStep result={result} /> : null}
      </div>
    </SectionShell>
  );
}
